"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GMType = exports.GMKind = exports.plugins = exports.default = void 0;
const just_clone_1 = __importDefault(require("just-clone"));
const mark_common_1 = require("@gojipress/mark-common");
const types_1 = require("./types");
Object.defineProperty(exports, "GMKind", { enumerable: true, get: function () { return types_1.GMKind; } });
Object.defineProperty(exports, "GMType", { enumerable: true, get: function () { return types_1.GMType; } });
const utils_1 = require("./utils");
const blankLine_1 = __importDefault(require("./plugins/block/leaf/blankLine"));
const paragraph_1 = __importDefault(require("./plugins/block/leaf/paragraph"));
const text_1 = __importDefault(require("./plugins/inline/text"));
function getLastChild(node) {
    if (node.children.length > 0) {
        return node.children[node.children.length - 1];
    }
    return node;
}
function getRelevantIndenter(node, depth) {
    if (depth <= 0) {
        return node;
    }
    let remainder = depth;
    let temp = node;
    while (0 < remainder) {
        temp = getLastChild(temp);
        if (temp.indenter) {
            remainder--;
        }
    }
    return temp;
}
function getRelevantLiteralContainer(node) {
    if (node.literalChildren) {
        return node;
    }
    const lastChild = getLastChild(node);
    if (lastChild === node) {
        return node;
    }
    return getRelevantLiteralContainer(lastChild);
}
const initiateBlockParserState = {
    unattachedChars: "",
    relevantPlugins: [],
    nextRelevantPlugins: [],
    literalContext: undefined,
    escape: false,
    ignoreNextNewlineChar: false,
    availableIndentContext: [],
    indentContext: 0,
    contextualLinePosition: 0,
    linePosition: 0,
};
Object.freeze(initiateBlockParserState);
const initiateInlineParserState = {
    unattachedChars: "",
    relevantPlugins: [],
    nextRelevantPlugins: [],
    literalContext: undefined,
    escape: false,
    context: [],
};
Object.freeze(initiateInlineParserState);
function resetParseCycleState(state, parser) {
    state.unattachedChars = "";
    state.relevantPlugins = [
        ...parser.blockPlugins,
        parser.fallbackBlockPlugin,
        blankLine_1.default,
    ];
}
function resetParseInlineCycleState(state, parser) {
    state.unattachedChars = "";
    state.relevantPlugins = [
        ...parser.inlinePlugins,
        parser.fallbackInlinePlugin,
    ];
    state.escape = false;
}
class GMParser {
    constructor(config) {
        this.config = {
            combineDoubleLineSeparators: false,
        };
        this.blockPlugins = [];
        this.fallbackBlockPlugin = paragraph_1.default;
        this.inlinePlugins = [];
        this.fallbackInlinePlugin = text_1.default;
        this.config = Object.assign(Object.assign({}, this.config), config);
    }
    use(plugin) {
        // The fallback plugins should not be added to the list of normal plugins
        // Users can run `setBlockFallback` and `setInlineFallback` to set different fallback plugins
        if (plugin === this.fallbackBlockPlugin || plugin === this.fallbackInlinePlugin) {
            return;
        }
        plugin.dependencies.forEach((dep) => this.use(dep));
        if (plugin.kind === types_1.GMKind.Block && !this.blockPlugins.includes(plugin)) {
            // Plugins added last are evaluated first. This allows custom plugins to override core plugins
            this.blockPlugins.unshift(plugin);
        }
        if (plugin.kind === types_1.GMKind.Inline && !this.inlinePlugins.includes(plugin)) {
            // Plugins added last are evaluated first. This allows custom plugins to override core plugins
            this.inlinePlugins.unshift(plugin);
        }
    }
    setBlockFallback(plugin) {
        if (this.blockPlugins.includes(plugin)) {
            throw new Error(`The ${plugin.name} plugin is already in the list of normal plugins.`);
        }
        this.fallbackBlockPlugin = plugin;
    }
    setInlineFallback(plugin) {
        if (this.inlinePlugins.includes(plugin)) {
            throw new Error(`The ${plugin.name} plugin is already in the list of normal plugins.`);
        }
        this.fallbackInlinePlugin = plugin;
    }
    parse(input) {
        // Initiate the state, tree, and relevant plugins
        const state = just_clone_1.default(initiateBlockParserState);
        const tree = {
            type: types_1.GMType.Root,
            children: [],
        };
        state.relevantPlugins = [...this.blockPlugins, this.fallbackBlockPlugin, blankLine_1.default];
        // let...of uses String.prototype[@@iterator](),
        // which correctly iterates over code points, even surrogate pairs
        for (let char of input) {
            // console.log("----------")
            // console.log(`char: ${char.replace(/\n/g, "\\n").replace(/ /g, "∘")}`)
            // console.log(`state.unattachedChars: ${state.unattachedChars.replace(/\n/g, "\\n").replace(/ /g, "∘")}`)
            const unattachedCharsArray = state.unattachedChars.split("\n");
            const unattachedCharsRest = unattachedCharsArray.slice(0, unattachedCharsArray.length - 1);
            const unattachedCharsLastLine = unattachedCharsArray[unattachedCharsArray.length - 1];
            // console.log(state.availableIndentContext)
            // console.log(`state.linePosition: ${state.linePosition}`);
            // console.log(stringOnlyContainsChars(unattachedCharsLastLine, whitespaces));
            // console.log(unattachedCharsLastLine.length === state.linePosition);
            if (state.indentContext) {
                state.contextualLinePosition = state.linePosition - state.availableIndentContext.slice(0, state.indentContext).reduce((total, current) => total + current);
            }
            else if (state.availableIndentContext.length &&
                state.availableIndentContext[0] <= (state.linePosition) &&
                mark_common_1.stringOnlyContainsChars(unattachedCharsLastLine, mark_common_1.whitespaces) &&
                unattachedCharsLastLine.length === state.linePosition) {
                if (mark_common_1.isWhiteSpaceChar(char)) {
                    state.unattachedChars = state.unattachedChars + char;
                    utils_1.setLinePosition(state, char);
                    continue;
                }
                const [indentContext, remainder] = mark_common_1.binsFilledWithRemainder(state.availableIndentContext, unattachedCharsLastLine.length);
                state.indentContext = indentContext;
                const trimmedLastLine = unattachedCharsLastLine.substring(unattachedCharsLastLine.length - remainder);
                state.unattachedChars = [...unattachedCharsRest, trimmedLastLine].join("\n");
                state.contextualLinePosition = remainder;
                if (state.contextualLinePosition === 0) {
                    state.relevantPlugins = [
                        ...this.blockPlugins,
                        this.fallbackBlockPlugin,
                        blankLine_1.default,
                    ];
                }
            }
            else if (state.availableIndentContext.length &&
                state.linePosition < state.availableIndentContext[0] &&
                mark_common_1.isWhiteSpaceChar(char) &&
                mark_common_1.stringOnlyContainsChars(unattachedCharsLastLine, mark_common_1.whitespaces) &&
                unattachedCharsLastLine.length === state.linePosition) {
                state.unattachedChars = state.unattachedChars + char;
                utils_1.setLinePosition(state, char);
                continue;
            }
            else {
                state.contextualLinePosition = state.linePosition;
                // console.log(`state.contextualLinePosition: ${state.contextualLinePosition}`)
            }
            if (this.config.combineDoubleLineSeparators && state.ignoreNextNewlineChar) {
                state.ignoreNextNewlineChar = false;
                if (mark_common_1.isLineEndingChar(char)) {
                    state.availableIndentContext = state.availableIndentContext.slice(0, state.indentContext);
                    state.linePosition = 0;
                    state.indentContext = 0;
                    continue;
                }
            }
            // console.log(`state.literalContext: ${JSON.stringify(state.literalContext, null, 2)}`)
            if (state.literalContext !== undefined) {
                // console.log(`state.indentContext: ${state.indentContext}`)
                // console.log(`state.unattachedChars: ${state.unattachedChars.replace(/\n/g, "\\n").replace(/ /g, "∘")}`)
                if (state.indentContext < state.literalContext.indentContext) {
                    if (mark_common_1.isLineEndingChar(char) ||
                        !mark_common_1.isWhiteSpaceChar(char)) {
                        // const relevantNode = getRelevantIndenter(tree, state.indentContext);
                        // const relevantLiteralNode = getRelevantLiteralContainer(relevantNode);
                        // if (state.unattachedChars.length > 0) {
                        //     relevantLiteralNode.children.push({
                        //       type: GMType.Text,
                        //       original: state.unattachedChars,
                        //       children: [],
                        //     });
                        //   } else if (state.indentContext) {
                        //     relevantLiteralNode.children.push({
                        //       type: GMType.Null,
                        //       children: [],
                        //     });
                        //   }
                        // if (state.unattachedChars.length === 1 && isLineEndingChar(state.unattachedChars)) {
                        //   relevantLiteralNode.children.push({
                        //     type: GMType.Text,
                        //     original: state.unattachedChars,
                        //     children: [],
                        //   });
                        //   state.unattachedChars = "";
                        // } else {
                        //   const lastLine = unattachedLines.pop();
                        //   const literalContent = unattachedLines.join("\n");
                        //   console.log("literalContent")
                        //   console.log(literalContent.replace(/\n/g, "\\n").replace(/ /g, "∘"))
                        //   if (state.unattachedChars.length > 0) {
                        //     if (literalContent.length > 0) {
                        //       relevantLiteralNode.children.push({
                        //         type: GMType.Text,
                        //         original: literalContent,
                        //         children: [],
                        //       });
                        //     } else if (state.indentContext) {
                        //       relevantNode.children.push({
                        //         type: GMType.Null,
                        //         children: [],
                        //       });
                        //     }
                        //     state.unattachedChars = lastLine as string;
                        //   }
                        // }
                        state.relevantPlugins = [
                            ...this.blockPlugins,
                            this.fallbackBlockPlugin,
                            blankLine_1.default,
                        ];
                        state.literalContext = undefined;
                    }
                }
                else {
                    // Blocks containing literal characters always ends with a closing fence on its own line
                    if (mark_common_1.isLineEndingChar(char) &&
                        state.unattachedChars.endsWith(state.literalContext.closer)) {
                        let content;
                        const contentArray = state.unattachedChars.split("\n");
                        const lastLine = contentArray.pop();
                        const lastLineContent = lastLine === null || lastLine === void 0 ? void 0 : lastLine.split(state.literalContext.closer)[0];
                        contentArray.push(lastLineContent || "");
                        content = contentArray.join("\n");
                        const relevantNode = getRelevantLiteralContainer(getRelevantIndenter(tree, state.indentContext));
                        if (content.length > 0) {
                            relevantNode.children.push({
                                type: types_1.GMType.Text,
                                original: content,
                                children: [],
                            });
                        }
                        else if (state.indentContext) {
                            relevantNode.children.push({
                                type: types_1.GMType.Text,
                                original: "",
                                children: [],
                            });
                        }
                        if (relevantNode.original && typeof relevantNode.original !== 'string') {
                            relevantNode.original.closing = state.literalContext.closer;
                        }
                        state.literalContext = undefined;
                        resetParseCycleState(state, this);
                    }
                    else if (mark_common_1.isLineEndingChar(char)) {
                        const relevantNode = getRelevantIndenter(tree, state.indentContext);
                        const relevantLiteralNode = getRelevantLiteralContainer(relevantNode);
                        relevantLiteralNode.children.push({
                            type: types_1.GMType.Text,
                            original: state.unattachedChars + char,
                            children: [],
                        });
                        resetParseCycleState(state, this);
                    }
                    else {
                        state.unattachedChars = state.unattachedChars + char;
                    }
                    utils_1.setLinePosition(state, char);
                    if (mark_common_1.isLineEndingChar(char)) {
                        state.indentContext = 0;
                    }
                    continue;
                }
            }
            // Reset the state for next character
            state.nextRelevantPlugins = [];
            let charCaptured = false;
            // console.log(`state.relevantPlugins: ${state.relevantPlugins.map(p => p.name).join(", ")}`)
            for (let i = 0; i < state.relevantPlugins.length; i++) {
                const result = state.relevantPlugins[i].charProcessor(char, state, (s) => this.parseInline(s));
                // console.log(`➯ ${state.relevantPlugins[i].name}.charProcessor ${result.nextPlugin ? '▼' : '∎'} ${result.charCaptured ? '☒' : '☐'} ${result.newContext ? '🆕' : ''}`);
                if (result.addition) {
                    const relevantNode = getRelevantIndenter(tree, state.indentContext);
                    relevantNode.children.push(result.addition);
                }
                if (result.newIndentContext !== undefined) {
                    state.indentContext = result.newIndentContext;
                }
                // If a char is captured, the next plugin should never be called
                // But there are cases where a plugin knows it's the only one applicable
                // but it doesn't want to capture the unattached characters in this cycle
                charCaptured = charCaptured || result.charCaptured;
                if (charCaptured || !result.nextPlugin) {
                    break;
                }
            }
            if (charCaptured) {
                resetParseCycleState(state, this);
            }
            else {
                state.unattachedChars = state.unattachedChars + char;
                state.relevantPlugins = [...state.nextRelevantPlugins];
            }
            if (this.config.combineDoubleLineSeparators && mark_common_1.isLineEndingChar(char)) {
                state.ignoreNextNewlineChar = true;
            }
            utils_1.setLinePosition(state, char);
            if (mark_common_1.isLineEndingChar(char)) {
                state.availableIndentContext = state.availableIndentContext.slice(0, state.indentContext);
                state.indentContext = 0;
            }
        }
        // We have reached the end of file (EOF)
        if (state.availableIndentContext.length &&
            state.availableIndentContext[0] <= state.linePosition &&
            mark_common_1.stringOnlyContainsChars(state.unattachedChars, mark_common_1.whitespaces) &&
            state.unattachedChars.length === state.linePosition) {
            const [indentContext, remainder] = mark_common_1.binsFilledWithRemainder(state.availableIndentContext, state.unattachedChars.length);
            state.indentContext = indentContext;
            state.unattachedChars = state.unattachedChars.substring(state.unattachedChars.length - remainder);
            state.contextualLinePosition = remainder;
        }
        else {
            state.contextualLinePosition = state.linePosition;
        }
        if (state.literalContext) {
            const validCloser = state.unattachedChars.endsWith(state.literalContext.closer);
            let content;
            if (validCloser) {
                const contentArray = state.unattachedChars.split("\n");
                const lastLine = contentArray.pop();
                const lastLineContent = lastLine === null || lastLine === void 0 ? void 0 : lastLine.split(state.literalContext.closer)[0];
                contentArray.push(lastLineContent || "");
                content = contentArray.join("\n");
            }
            else {
                content = state.unattachedChars;
            }
            const relevantNode = getRelevantLiteralContainer(getRelevantIndenter(tree, state.indentContext));
            if (content.length > 0) {
                relevantNode.children.push({
                    type: types_1.GMType.Text,
                    original: content,
                    children: [],
                });
            }
            else if (state.indentContext) {
                relevantNode.children.push({
                    type: types_1.GMType.Text,
                    original: content,
                    children: [],
                });
            }
            if (validCloser && relevantNode.original && typeof relevantNode.original !== 'string') {
                relevantNode.original.closing = state.literalContext.closer;
            }
        }
        else if (state.unattachedChars.length > 0) {
            for (let i = 0; i < state.relevantPlugins.length; i++) {
                const result = state.relevantPlugins[i].EOFProcessor(state, (s) => this.parseInline(s));
                if (result.addition) {
                    getRelevantIndenter(tree, state.indentContext).children.push(result.addition);
                }
                if (!result.nextPlugin) {
                    break;
                }
            }
        }
        else if (state.indentContext > 0) {
            const relevantNode = getRelevantIndenter(tree, state.indentContext);
            relevantNode.children.push({
                type: types_1.GMType.Blank,
                original: "",
                children: [],
            });
        }
        return tree;
    }
    parseInline(input) {
        var _a, _b, _c, _d, _e, _f;
        // Initiate the state, list, and relevant plugins
        const state = Object.assign({}, initiateInlineParserState);
        let list = [];
        state.relevantPlugins = [...this.inlinePlugins, this.fallbackInlinePlugin];
        state.nextRelevantPlugins = [];
        // let...of uses String.prototype[@@iterator](),
        // which correctly iterates over code points, even surrogate pairs
        for (let char of input) {
            // A literal context continues until the closer string is encountered
            if (state.literalContext) {
                state.unattachedChars = state.unattachedChars + char;
                if (state.unattachedChars.endsWith(state.literalContext.closer)) {
                    (_b = (_a = state.literalContext.plugin).closeProcessor) === null || _b === void 0 ? void 0 : _b.call(_a, state, list, true);
                    state.literalContext = undefined;
                    resetParseInlineCycleState(state, this);
                }
                continue; // continues the `for (let char of input)` loop
            }
            if (state.escape) {
                list.push({
                    type: types_1.GMType.Literal,
                    original: char,
                    children: [],
                });
                resetParseInlineCycleState(state, this);
                continue; // continues the `for (let char of input)` loop
            }
            // Check if we should escape the next character
            if (char === "\\") {
                this.fallbackInlinePlugin.closeProcessor(state, list, false);
                // Do not add the backslash to state.unattachedChars
                state.escape = true;
                continue; // continues the `for (let char of input)` loop
            }
            // If there are open contexts, check if we should close them
            if (state.context.length > 0) {
                const shouldCloseContext = state.context.some(
                // If the fences requires multiple characters
                // then change this to context => state.unattachedChars.endsWith(context.closer)
                (context) => context.closer === char);
                if (shouldCloseContext) {
                    let closingContext;
                    do {
                        closingContext = state.context.pop();
                        list = closingContext.plugin.contextCloseProcessor(state, list, closingContext.list, closingContext.closer === char);
                        state.unattachedChars = "";
                    } while (closingContext.closer !== char);
                    resetParseInlineCycleState(state, this);
                    continue; // continues the `for (let char of input)` loop
                }
            }
            // If the fallback plugin is the only relevant one,
            // We need to check for openings for other plugins
            // Otherwise, once the fallback plugin is used,
            // it will be used until the end of line
            if (state.relevantPlugins.length === 1 &&
                state.relevantPlugins.includes(this.fallbackInlinePlugin)) {
                let relevantPlugins = [...this.inlinePlugins];
                for (let i = 0; i < relevantPlugins.length; i++) {
                    const relevant = relevantPlugins[i].openingDetector(char, state, list);
                    // If the current character is part of the opening for a new inline element
                    // Then put all unattached characters into a Text element
                    // And carry on processing the character
                    if (relevant) {
                        state.relevantPlugins = [relevantPlugins[i]];
                        (_d = (_c = this.fallbackInlinePlugin).closeProcessor) === null || _d === void 0 ? void 0 : _d.call(_c, state, list, true);
                        state.unattachedChars = "";
                        break; // Break the closest for loop but carry on with the outer for...of loop
                    }
                }
            }
            // Reset the state for next character
            state.nextRelevantPlugins = [];
            let charCaptured = false;
            for (let i = 0; i < state.relevantPlugins.length; i++) {
                const result = state.relevantPlugins[i].charProcessor(char, state, list);
                charCaptured = charCaptured || result.charCaptured;
                if (result.newContext) {
                    list = [];
                }
                if (!result.nextPlugin) {
                    break;
                }
            }
            if (charCaptured) {
                resetParseInlineCycleState(state, this);
            }
            else {
                state.unattachedChars = state.unattachedChars + char;
                state.relevantPlugins = [...state.nextRelevantPlugins];
            }
        }
        // We have reached the end of the input
        // Close literal context first
        if (state.literalContext) {
            const proper = state.unattachedChars.endsWith(state.literalContext.closer);
            (_f = (_e = state.literalContext.plugin).closeProcessor) === null || _f === void 0 ? void 0 : _f.call(_e, state, list, proper);
            state.literalContext = undefined;
            state.unattachedChars = "";
        }
        if (state.escape) {
            list.push({
                type: types_1.GMType.Text,
                original: "\\",
                children: [],
            });
            return list;
        }
        if (state.context.length > 0) {
            let closingContext;
            while ((closingContext = state.context.pop())) {
                list = closingContext.plugin.contextCloseProcessor(state, list, closingContext.list, false);
                state.unattachedChars = "";
            }
        }
        if (state.unattachedChars.length > 0) {
            for (let i = 0; i < state.relevantPlugins.length; i++) {
                const nextPlugin = state.relevantPlugins[i].EOFProcessor(state, list);
                if (!nextPlugin) {
                    break;
                }
            }
        }
        return list;
    }
}
exports.default = GMParser;
const plugins = {
    blankLine: blankLine_1.default,
    paragraph: paragraph_1.default,
    text: text_1.default,
};
exports.plugins = plugins;
